"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

const variants = [
  {
    id: "red",
    name: "Red Line",
    descriptor: "Grounded",
    strain: "Red Maeng Da · Single Origin",
    color: "#8B3A3A",
    border: "rgba(139,58,58,0.3)",
    bg: "rgba(139,58,58,0.04)",
    description: "A deeper, more settled experience. Perfect for evenings, decompression, and those who prefer a more deliberate pace.",
    details: [
      "Red Maeng Da — Single-origin",
      "Fine-milled powder",
      "Batch tested: KR-2026-002",
      "In stock — ships in 24h",
    ],
  },
  {
    id: "white",
    name: "White Line",
    descriptor: "Focused",
    strain: "White Maeng Da · Single Origin",
    color: "#4A5C6B",
    border: "rgba(74,92,107,0.3)",
    bg: "rgba(74,92,107,0.04)",
    description: "Clean and sharp. For mornings, work sessions, and any time you need to be fully present without the edges.",
    details: [
      "White Maeng Da — Single-origin",
      "Fine-milled powder",
      "Batch tested: KR-2025-002",
      "In stock — ships in 24h",
    ],
  },
  {
    id: "green",
    name: "Green Line",
    descriptor: "Balanced",
    strain: "Green Maeng Da · Single Origin",
    color: "#3F5A45",
    border: "rgba(63,90,69,0.3)",
    bg: "rgba(63,90,69,0.04)",
    description: "The all-day companion. Smooth, even, and consistent. The line most users come back to first — and keep.",
    details: [
      "Green Maeng Da — Single-origin",
      "Fine-milled powder",
      "Batch tested: KR-2026-001",
      "In stock — ships in 24h",
    ],
  },
];

export default function ShopInteractive() {
  const [selectedVariant, setSelectedVariant] = useState(variants[2]); // Green default
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showStickyCart, setShowStickyCart] = useState(false);
  const [cartItems, setCartItems] = useState<{ variant: string; qty: number }[]>([]);
  const [showCart, setShowCart] = useState(false);
  const mainCTARef = useRef<HTMLButtonElement>(null);

  const PRICE = 89.99;
  const totalPrice = (PRICE * quantity).toFixed(2);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCart(!entry.isIntersecting),
      { threshold: 0.5 }
    );
    if (mainCTARef.current) observer.observe(mainCTARef.current);
    return () => observer.disconnect();
  }, []);

  const handleAddToCart = () => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.variant === selectedVariant.name);
      if (existing) {
        return prev.map((i) =>
          i.variant === selectedVariant.name ? { ...i, qty: i.qty + quantity } : i
        );
      }
      return [...prev, { variant: selectedVariant.name, qty: quantity }];
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  return (
    <div style={{ backgroundColor: "#F6F5F2" }}>
      {/* Cart button (top right) */}
      <div className="fixed top-20 right-4 z-50 md:right-8">
        <button
          onClick={() => setShowCart(!showCart)}
          className="relative w-12 h-12 flex items-center justify-center bg-white shadow-card transition-all hover:shadow-card-hover"
          style={{ border: "1px solid #E0DFDB", borderRadius: "2px" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {cartItems.length > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold"
              style={{ backgroundColor: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              {cartItems.reduce((a, b) => a + b.qty, 0)}
            </span>
          )}
        </button>
      </div>

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setShowCart(false)}
          />
          <div
            className="w-full max-w-sm flex flex-col"
            style={{ backgroundColor: "#F6F5F2", borderLeft: "1px solid #E0DFDB" }}
          >
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: "#E0DFDB" }}>
              <h3
                className="font-serif font-semibold text-lg"
                style={{ fontFamily: "Fraunces, Georgia, serif", color: "#1F1F1F", letterSpacing: "-0.02em" }}
              >
                Your Cart
              </h3>
              <button onClick={() => setShowCart(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6L18 18M18 6L6 18" stroke="#1F1F1F" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-6 overflow-y-auto">
              {cartItems.length === 0 ? (
                <p className="text-sm text-center py-12" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                  Your cart is empty.
                </p>
              ) : (
                <div className="space-y-4">
                  {cartItems.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4"
                      style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                    >
                      <div>
                        <div className="text-sm font-semibold" style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}>
                          Kratapillar — {item.variant}
                        </div>
                        <div className="text-xs" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                          1 Kilo · Qty: {item.qty}
                        </div>
                      </div>
                      <div className="text-sm font-semibold" style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}>
                        ${(PRICE * item.qty).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cartItems.length > 0 && (
              <div className="p-6 border-t" style={{ borderColor: "#E0DFDB" }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold" style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}>
                    Subtotal
                  </span>
                  <span className="font-serif font-bold text-xl" style={{ fontFamily: "Fraunces, Georgia, serif", color: "#1F1F1F" }}>
                    ${cartItems.reduce((a, b) => a + PRICE * b.qty, 0).toFixed(2)}
                  </span>
                </div>
                <button className="btn-primary w-full justify-center text-sm">
                  Proceed to Checkout
                </button>
                <p className="text-xs text-center mt-3" style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}>
                  Free shipping on all orders · 30-day guarantee
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main product layout */}
      <div className="max-w-container mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          {/* Left: Product Visual */}
          <div className="relative">
            {/* Product image */}
            <div
              className="aspect-square relative overflow-hidden"
              style={{
                backgroundColor: selectedVariant.bg,
                border: `1.5px solid ${selectedVariant.border}`,
                transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              <AppImage
                src="/assets/images/Untitled-1772515820182.jpeg"
                alt="Kratapillar Premium Kratom Powder 1 Kilo — single-origin, lab-verified, fine-milled botanical powder in resealable kraft packaging"
                fill
                className="object-cover"
                priority
              />

              {/* Badges overlay */}
              <div className="absolute top-4 left-4 space-y-2">
                <div className="badge-sample">Free Sample Included</div>
              </div>

              {/* Line color indicator */}
              <div
                className="absolute bottom-4 right-4 px-3 py-1.5 flex items-center gap-2 bg-white"
                style={{ border: "1px solid #E0DFDB" }}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: selectedVariant.color }}
                />
                <span
                  className="text-xs font-semibold"
                  style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {selectedVariant.name}
                </span>
              </div>
            </div>

            {/* Trust badges below image */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { label: "Lab Tested", icon: "🧪" },
                { label: "Free Sample", icon: "🎁" },
                { label: "30-Day Guarantee", icon: "✅" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="p-3 text-center"
                  style={{ backgroundColor: "white", border: "1px solid #E0DFDB" }}
                >
                  <div className="text-lg mb-1">{item.icon}</div>
                  <div
                    className="text-xs font-medium"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Product Info */}
          <div>
            {/* Breadcrumb */}
            <div
              className="flex items-center gap-2 text-xs mb-6"
              style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              <Link href="/homepage" className="hover:text-charcoal transition-colors">Home</Link>
              <span>/</span>
              <span style={{ color: "#1F1F1F" }}>Shop</span>
            </div>

            {/* Product name */}
            <h1
              className="font-serif font-bold mb-2"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
                letterSpacing: "-0.025em",
                lineHeight: "1.1",
                color: "#1F1F1F",
              }}
            >
              Kratapillar Premium
              <br />
              Kratom Powder
            </h1>
            <p
              className="text-base mb-6"
              style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              1 Kilo · Single-Origin · Third-Party Lab Tested
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-8">
              <span
                className="font-serif font-bold"
                style={{
                  fontFamily: "Fraunces, Georgia, serif",
                  fontSize: "2.5rem",
                  letterSpacing: "-0.03em",
                  color: "#1F1F1F",
                }}
              >
                ${PRICE}
              </span>
              <span
                className="text-sm"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                / 1 Kilo · Free Shipping
              </span>
            </div>

            {/* Divider */}
            <hr className="divider-olive mb-8" />

            {/* Why Kratapillar — Value Props */}
            <div className="mb-8">
              <div
                className="grid grid-cols-5 gap-2"
                style={{ borderTop: "1px solid #E0DFDB", borderBottom: "1px solid #E0DFDB", paddingTop: "16px", paddingBottom: "16px" }}
              >
                {[
                  { icon: "🧪", title: "Lab Tested", desc: "Every batch tested by Cascadia Labs. Full COA published." },
                  { icon: "🌱", title: "Single Origin", desc: "One source, one farm. Full traceability from soil to shelf." },
                  { icon: "📦", title: "Free Shipping", desc: "Free standard shipping on every order." },
                  { icon: "🎁", title: "Free Sample", desc: "Try before you commit. Sample included with first order." },
                  { icon: "✅", title: "30-Day Guarantee", desc: "Not satisfied? Full refund within 30 days." },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col items-center text-center px-1"
                  >
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-full mb-2 text-base flex-shrink-0"
                      style={{ backgroundColor: "rgba(63,90,69,0.08)" }}
                    >
                      {item.icon}
                    </div>
                    <div
                      className="text-xs font-semibold mb-1 leading-tight"
                      style={{ color: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.title}
                    </div>
                    <div
                      className="text-xs leading-snug hidden lg:block"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Variant selector */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label
                  className="text-xs font-semibold uppercase tracking-widest"
                  style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  Select Line
                </label>
                <span
                  className="text-xs"
                  style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  {selectedVariant.name} — <em>{selectedVariant.descriptor}</em>
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVariant(v)}
                    className="variant-card text-left"
                    style={{
                      borderColor: selectedVariant.id === v.id ? v.color : "#E0DFDB",
                      backgroundColor: selectedVariant.id === v.id ? v.bg : "white",
                    }}
                  >
                    <div
                      className="w-8 h-1 mb-3"
                      style={{ backgroundColor: v.color }}
                    />
                    <div
                      className="text-xs font-semibold uppercase tracking-wide mb-1"
                      style={{ color: v.color, fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {v.name}
                    </div>
                    <div
                      className="font-serif font-semibold text-base"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        color: "#1F1F1F",
                        letterSpacing: "-0.015em",
                      }}
                    >
                      {v.descriptor}
                    </div>
                    <div
                      className="mt-1"
                      style={{
                        fontSize: "0.72em",
                        color: "#4B5D6E",
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontWeight: 400,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {v.strain}
                    </div>
                  </button>
                ))}
              </div>

              {/* Variant description */}
              <div
                className="mt-4 p-4 text-sm leading-relaxed"
                style={{
                  backgroundColor: selectedVariant.bg,
                  border: `1px solid ${selectedVariant.border}`,
                  color: "#6B6B6B",
                  fontFamily: "Inter, system-ui, sans-serif",
                  transition: "all 0.3s ease",
                }}
              >
                {selectedVariant.description}
              </div>
            </div>

            {/* Quantity selector */}
            <div className="mb-8">
              <label
                className="text-xs font-semibold uppercase tracking-widest mb-3 block"
                style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                Quantity
              </label>
              <div className="flex items-center gap-0">
                <button
                  className="qty-btn"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <div
                  className="w-12 h-9 flex items-center justify-center text-sm font-semibold border-y"
                  style={{
                    borderColor: "#E0DFDB",
                    color: "#1F1F1F",
                    fontFamily: "Inter, system-ui, sans-serif",
                  }}
                >
                  {quantity}
                </div>
                <button
                  className="qty-btn"
                  onClick={() => setQuantity((q) => q + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
                <span
                  className="ml-4 text-sm"
                  style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  = <strong style={{ color: "#1F1F1F" }}>${totalPrice}</strong> total
                </span>
              </div>
            </div>

            {/* Read Before Use Warning */}
            <div
              style={{
                backgroundColor: "#FFF8E1",
                borderLeft: "4px solid #FF8F00",
                border: "1px solid #FFE082",
                borderLeftWidth: "4px",
                borderLeftColor: "#FF8F00",
                borderRadius: "4px",
                padding: "10px 12px",
                marginBottom: "10px",
              }}
            >
              <p
                style={{
                  fontSize: "9px",
                  lineHeight: "1.5",
                  color: "#5D4037",
                  fontFamily: "Inter, system-ui, sans-serif",
                  margin: 0,
                }}
              >
                ⚠️ <strong>Read Before Use:</strong> Keep out of the reach of children. Do not use if pregnant or nursing. Do not use while operating heavy machinery. Product may interact with other medications or substances. Please consult your physician or qualified healthcare professional prior to use. This product may be habit-forming.
              </p>
            </div>
            <p
              style={{
                fontSize: "8px",
                lineHeight: "1.5",
                color: "#6B6B6B",
                fontFamily: "Inter, system-ui, sans-serif",
                marginBottom: "12px",
                marginTop: 0,
              }}
            >
              Not intended for sale to persons under the age of 21. These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.
            </p>

            {/* Add to cart */}
            <button
              ref={mainCTARef}
              onClick={handleAddToCart}
              className="btn-primary w-full justify-center text-sm mb-4"
              style={{
                backgroundColor: addedToCart ? "#2d4231" : "#3F5A45",
                padding: "1.125rem",
                fontSize: "0.9375rem",
                transition: "all 0.3s ease",
              }}
            >
              {addedToCart ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Added to Cart
                </>
              ) : (
                <>
                  Add to Cart — ${totalPrice}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>

            {/* Guarantee badge */}
            <div className="guarantee-badge mb-8">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: "2px" }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 12l2 2 4-4" stroke="#3F5A45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <div
                  className="text-sm font-semibold mb-0.5"
                  style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  30-Day Money-Back Guarantee
                </div>
                <div
                  className="text-xs leading-relaxed"
                  style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                >
                  Not satisfied? We refund you. No forms, no hoops, no questions.
                </div>
              </div>
            </div>

            {/* Product details */}
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-widest mb-4"
                style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                Product Details
              </p>
              <ul className="space-y-2">
                {selectedVariant.details.map((detail, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-sm"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="#3F5A45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>

            {/* Supplement Facts Panel */}
            <div
              className="mt-10"
              style={{
                border: "2px solid #1F1F1F",
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: "#FFFFFF",
                maxWidth: "480px",
              }}
            >
              {/* Header */}
              <div style={{ borderBottom: "8px solid #1F1F1F", padding: "8px 10px 6px" }}>
                <div
                  style={{
                    fontSize: "1.75rem",
                    fontWeight: 900,
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                    color: "#1F1F1F",
                    fontFamily: "'Arial Black', Arial, sans-serif",
                  }}
                >
                  Supplement Facts
                </div>
              </div>

              {/* Serving info */}
              <div style={{ padding: "6px 10px", borderBottom: "4px solid #1F1F1F" }}>
                <div style={{ fontSize: "0.7rem", color: "#1F1F1F", lineHeight: 1.6 }}>
                  <span>Serving Size: <strong>2.4g (1 level teaspoon)</strong></span>
                  <br />
                  <span>Servings Per Container: <strong>~416</strong></span>
                </div>
              </div>

              {/* Column headers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: "4px",
                  padding: "4px 10px",
                  borderBottom: "2px solid #1F1F1F",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  color: "#1F1F1F",
                  textAlign: "right",
                }}
              >
                <div style={{ textAlign: "left" }}>&nbsp;</div>
                <div style={{ minWidth: "100px" }}>Amount Per Serving</div>
                <div style={{ minWidth: "60px" }}>% Daily Value</div>
              </div>

              {/* Active Ingredient header row */}
              <div
                style={{
                  padding: "5px 10px 2px",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#1F1F1F",
                  borderBottom: "1px solid #1F1F1F",
                  fontFamily: "'Arial', sans-serif",
                }}
              >
                Active Ingredient
              </div>

              {/* Kratom row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: "4px",
                  padding: "4px 10px",
                  borderBottom: "1px solid #1F1F1F",
                  fontSize: "0.65rem",
                  color: "#1F1F1F",
                  alignItems: "start",
                }}
              >
                <div>
                  <strong style={{ fontSize: "0.68rem" }}>Mitragyna speciosa</strong>
                  <em style={{ fontSize: "0.62rem" }}> (Kratom) Leaf Powder</em>
                </div>
                <div style={{ textAlign: "right", minWidth: "100px", fontWeight: 700 }}>2.4g</div>
                <div style={{ textAlign: "right", minWidth: "60px" }}>†</div>
              </div>

              {/* Mitragynine sub-row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: "4px",
                  padding: "3px 10px 3px 20px",
                  borderBottom: "1px solid #CCCCCC",
                  fontSize: "0.63rem",
                  color: "#1F1F1F",
                  alignItems: "center",
                }}
              >
                <div>— Mitragynine (1.4%)</div>
                <div style={{ textAlign: "right", minWidth: "100px" }}>33.6 mg</div>
                <div style={{ textAlign: "right", minWidth: "60px" }}>†</div>
              </div>

              {/* Daily value footnote */}
              <div style={{ padding: "4px 10px", borderBottom: "2px solid #1F1F1F", fontSize: "0.58rem", color: "#1F1F1F" }}>
                † Daily Value not established.
              </div>

              {/* Other ingredients */}
              <div style={{ padding: "6px 10px", fontSize: "0.63rem", color: "#1F1F1F", lineHeight: 1.5 }}>
                <strong>Other Ingredients:</strong> Mitragyna speciosa leaf powder.
              </div>
            </div>

            {/* Directions, storage, manufactured-for below panel */}
            <div className="mt-4" style={{ maxWidth: "480px", fontFamily: "Inter, system-ui, sans-serif" }}>
              <p style={{ fontSize: "0.72rem", color: "#1F1F1F", lineHeight: 1.6, marginBottom: "6px" }}>
                <strong>Direction for use:</strong> Take one serving (2.4g) daily. Do not exceed two servings in 24 hours.
              </p>
              <p style={{ fontSize: "0.72rem", color: "#6B6B6B", lineHeight: 1.6, marginBottom: "6px" }}>
                Store in a cool, dry place. Keep out of reach of children.
              </p>
              <p style={{ fontSize: "0.72rem", color: "#6B6B6B", lineHeight: 1.6, marginBottom: "8px" }}>
                Manufactured for: Kratapillar [address TBD]
              </p>
            </div>

            {/* COA link below panel */}
            <div className="mt-1" style={{ maxWidth: "480px" }}>
              <p style={{ fontSize: "0.72rem", color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif", lineHeight: 1.5 }}>
                Full lab results (COAs) available on our{" "}
                <Link
                  href="/lab-testing"
                  style={{
                    color: "#3F5A45",
                    fontWeight: 600,
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                  }}
                >
                  Lab Testing page
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Add to Cart */}
      <div
        className={`sticky-cart ${showStickyCart ? "visible" : ""}`}
        style={{ backgroundColor: "white" }}
      >
        <div className="max-w-container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: selectedVariant.color }}
            />
            <div>
              <div
                className="text-sm font-semibold"
                style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                Kratapillar — {selectedVariant.name}
              </div>
              <div
                className="text-xs"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                1 Kilo · {selectedVariant.descriptor}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span
              className="font-serif font-bold text-xl"
              style={{ fontFamily: "Fraunces, Georgia, serif", color: "#1F1F1F" }}
            >
              ${totalPrice}
            </span>
            <button
              onClick={handleAddToCart}
              className="btn-primary text-xs py-2.5 px-5"
            >
              {addedToCart ? "Added ✓" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}